import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Search, Plus, Edit, Trash2, Eye, X, AlertCircle, 
  CheckCircle, RefreshCw, ChevronLeft, ChevronRight, Clock
} from 'lucide-react';
import { packagesAPI, labTestsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const TestPackages = () => {
  const [packages, setPackages] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testSearchQuery, setTestSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    is_available: true,
    accepts_home_visit: false,
    has_expiry: false,
    expiry_date: '',
    tests: []
  });

  const limit = 12;

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit };
      if (searchQuery) params.search = searchQuery;
      if (availabilityFilter !== 'all') params.is_available = availabilityFilter === 'available';
      
      const response = await packagesAPI.getAll(params);
      const data = response.data?.data || response.data;
      setPackages(data.items || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.total || 0);
    } catch (err) {
      toast.error('حدث خطأ أثناء تحميل الباقات');
    }
    setLoading(false);
  }, [currentPage, searchQuery, availabilityFilter]);

  const fetchAllTests = useCallback(async () => {
    try {
      const response = await labTestsAPI.getAll({ limit: 500 });
      const data = response.data?.data;
      setAllTests(data?.items || []);
    } catch (err) {
      console.log('Error fetching tests:', err);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  useEffect(() => {
    fetchAllTests();
  }, [fetchAllTests]);

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      description: '',
      is_available: true,
      accepts_home_visit: false,
      has_expiry: false,
      expiry_date: '',
      tests: []
    });
    setSelectedPackage(null);
    setTestSearchQuery('');
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (pkg) => {
    setSelectedPackage(pkg);
    setFormData({
      name: pkg.name || '',
      price: pkg.price || '',
      description: pkg.description || '',
      is_available: pkg.is_available ?? true,
      accepts_home_visit: pkg.accepts_home_visit === true,
      has_expiry: pkg.has_expiry || false,
      expiry_date: pkg.expiry_date ? new Date(pkg.expiry_date).toISOString().slice(0, 16) : '',
      tests: pkg.tests?.map(t => t._id) || []
    });
    setShowModal(true);
  };

  const handleOpenView = (pkg) => {
    setSelectedPackage(pkg);
    setShowViewModal(true);
  };

  const handleOpenDelete = (pkg) => {
    setSelectedPackage(pkg);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('اسم الباقة مطلوب');
      return;
    }
    if (!formData.price || formData.price <= 0) {
      toast.error('السعر مطلوب');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        price: parseFloat(formData.price),
        expiry_date: formData.has_expiry && formData.expiry_date ? formData.expiry_date : null
      };

      if (selectedPackage) {
        await packagesAPI.update(selectedPackage._id, dataToSend);
        toast.success('تم تحديث الباقة بنجاح');
      } else {
        await packagesAPI.create(dataToSend);
        toast.success('تم إنشاء الباقة بنجاح');
      }
      setShowModal(false);
      resetForm();
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await packagesAPI.delete(selectedPackage._id);
      toast.success('تم حذف الباقة بنجاح');
      setShowDeleteModal(false);
      setSelectedPackage(null);
      fetchPackages();
    } catch (err) {
      toast.error('حدث خطأ أثناء الحذف');
    }
    setDeleting(false);
  };

  const toggleTestSelection = (testId) => {
    setFormData(prev => ({
      ...prev,
      tests: prev.tests.includes(testId)
        ? prev.tests.filter(id => id !== testId)
        : [...prev.tests, testId]
    }));
  };

  const filteredTests = allTests.filter(test => 
    test.name_en?.toLowerCase().includes(testSearchQuery.toLowerCase())
  );

  const selectedTests = allTests.filter(test => formData.tests.includes(test._id));

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (pkg) => {
    if (!pkg.has_expiry || !pkg.expiry_date) return false;
    return new Date(pkg.expiry_date) < new Date();
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#1E293B] flex items-center justify-center shadow-lg">
            <Package className="w-6 h-6 text-slate-100" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B]">باقات التحاليل</h1>
            <p className="text-slate-500 text-sm">{totalCount} باقة</p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleOpenCreate}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>إنشاء باقة جديدة</span>
        </motion.button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder="البحث باسم الباقة..."
              className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          <select
            value={availabilityFilter}
            onChange={(e) => { setAvailabilityFilter(e.target.value); setCurrentPage(1); }}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
          >
            <option value="all">جميع الحالات</option>
            <option value="available">متاح</option>
            <option value="unavailable">غير متاح</option>
          </select>
          <button
            onClick={fetchPackages}
            className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Packages Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : packages.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">لا توجد باقات</p>
          <button onClick={handleOpenCreate} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl">
            إنشاء باقة جديدة
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {packages.map((pkg) => (
              <motion.div
                key={pkg._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{pkg.name}</h3>
                        <p className="text-xs text-gray-400">ID: {pkg._id?.slice(-6)}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        isExpired(pkg) 
                          ? 'bg-red-100 text-red-600'
                          : pkg.is_available 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {isExpired(pkg) ? 'منتهي' : pkg.is_available ? 'متاح' : 'غير متاح'}
                      </span>
                      {pkg.accepts_home_visit && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          زيارة منزلية
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1 text-blue-600">
                      <span className="text-lg font-bold">{pkg.price?.toFixed(2)}</span>
                      <span className="text-sm">ر.س</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-500 text-sm">
                      <span className="font-medium">{pkg.testsCount || 0}</span>
                      <span>تحليل</span>
                    </div>
                  </div>

                  {pkg.has_expiry && pkg.expiry_date && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                      <Clock className="w-4 h-4" />
                      <span>تنتهي: {formatDate(pkg.expiry_date)}</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenView(pkg)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      <span>التفاصيل</span>
                    </button>
                    <button
                      onClick={() => handleOpenEdit(pkg)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 text-sm"
                    >
                      <Edit className="w-4 h-4" />
                      <span>تعديل</span>
                    </button>
                    <button
                      onClick={() => handleOpenDelete(pkg)}
                      className="flex items-center justify-center px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-sm text-gray-600">
                {currentPage} من {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-[#1E293B]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                    <Package className="w-6 h-6" />
                    {selectedPackage ? 'تعديل الباقة' : 'إنشاء باقة جديدة'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      اسم الباقة <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                      placeholder="مثال: باقة الشتاء الشاملة"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      السعر (ر.س) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">الوصف</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 resize-none"
                    rows={3}
                    placeholder="وصف الباقة..."
                  />
                </div>

                <div className="flex items-center gap-6 mb-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_available}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_available: e.target.checked }))}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">متاحة للحجز</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.has_expiry}
                      onChange={(e) => setFormData(prev => ({ ...prev, has_expiry: e.target.checked }))}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">تحديد تاريخ انتهاء</span>
                  </label>
                </div>

                <div className="flex items-center gap-6 mb-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.accepts_home_visit}
                      onChange={(e) => setFormData(prev => ({ ...prev, accepts_home_visit: e.target.checked }))}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">قبول الزيارات المنزلية لهذه الباقة</span>
                  </label>
                </div>

                {formData.has_expiry && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      تاريخ ووقت الانتهاء
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                      className="w-full md:w-1/2 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                    />
                  </div>
                )}

                {/* Tests Selection */}
                <div className="border-t border-gray-100 pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">اختيار التحاليل</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Available Tests */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-600">التحاليل المتاحة</h4>
                        <span className="text-xs text-gray-400">{filteredTests.length} تحليل</span>
                      </div>
                      <div className="relative mb-3">
                        <input
                          type="text"
                          value={testSearchQuery}
                          onChange={(e) => setTestSearchQuery(e.target.value)}
                          placeholder="البحث عن تحليل..."
                          className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      </div>
                      <div className="border border-gray-200 rounded-xl h-64 overflow-y-auto">
                        {filteredTests.length === 0 ? (
                          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                            لا توجد تحاليل
                          </div>
                        ) : (
                          filteredTests.map(test => (
                            <label
                              key={test._id}
                              className={`flex items-center gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${
                                formData.tests.includes(test._id) ? 'bg-blue-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.tests.includes(test._id)}
                                onChange={() => toggleTestSelection(test._id)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600"
                              />
                              <span className="text-sm text-gray-700">{test.name_en}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Selected Tests */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-600">التحاليل المختارة</h4>
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                          {selectedTests.length} تحليل
                        </span>
                      </div>
                      <div className="border border-gray-200 rounded-xl h-72 overflow-y-auto">
                        {selectedTests.length === 0 ? (
                          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                            لم يتم اختيار أي تحليل
                          </div>
                        ) : (
                          selectedTests.map(test => (
                            <div
                              key={test._id}
                              className="flex items-center justify-between px-4 py-3 border-b border-gray-50 hover:bg-gray-50"
                            >
                              <span className="text-sm text-gray-700">{test.name_en}</span>
                              <button
                                onClick={() => toggleTestSelection(test._id)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>جاري الحفظ...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>حفظ</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Modal */}
      <AnimatePresence>
        {showViewModal && selectedPackage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-[#1E293B]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-100">تفاصيل الباقة</h2>
                  <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-blue-600 mb-2">{selectedPackage.name}</h3>
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-lg font-semibold text-gray-700">
                      السعر: {selectedPackage.price?.toFixed(2)} ر.س
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedPackage.is_available 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedPackage.is_available ? 'متاح' : 'غير متاح'}
                    </span>
                  </div>
                </div>

                {selectedPackage.description && (
                  <p className="text-gray-600 text-center mb-6">{selectedPackage.description}</p>
                )}

                <div className="bg-blue-50 rounded-xl p-4 text-center mb-6">
                  <p className="text-blue-700">
                    تتضمن هذه الباقة <span className="font-bold">{selectedPackage.testsCount || 0}</span> تحليل
                  </p>
                </div>

                {selectedPackage.tests && selectedPackage.tests.length > 0 && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">اسم التحليل</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">نوع العينة</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">القسم</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPackage.tests.map((test, index) => (
                          <tr key={test._id || index} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm text-gray-700">{test.name_en || '-'}</td>
                            <td className="py-3 px-4 text-sm text-gray-500">{test.specimen_type || '-'}</td>
                            <td className="py-3 px-4 text-sm text-gray-500">{test.department || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-5 py-2.5 bg-gray-600 text-white rounded-xl hover:bg-gray-700"
                >
                  إغلاق
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedPackage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">حذف الباقة</h3>
                <p className="text-gray-500 mb-6">
                  هل أنت متأكد من حذف باقة "{selectedPackage.name}"؟ هذا الإجراء لا يمكن التراجع عنه.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deleting ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="w-5 h-5" />
                        <span>حذف</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default TestPackages;

